'use client';

import React from 'react';

import { Button, Checkbox, Icon } from '@theme/components';
import { Facet, FacetChoice, SortOption } from '@akinon/next/types';
import { CategorySorter } from './category-sorter';
import { useLocalization } from '@akinon/next/hooks';
import { useAppDispatch, useAppSelector } from '@akinon/next/redux/hooks';
import { toggleCompare } from '@theme/redux/reducers/compare';
import { useGroupedFacets } from './facet-grouping';

interface Props {
  totalCount: number;
  setMenuStatus: () => void;
  sortOptions: SortOption[];
  selectedChoicesCount?: number;
  selectedFacets?: Facet[];
  onRemoveFilter?: (facet: Facet, choice: FacetChoice) => void;
}

export const CategoryHeader = (props: Props) => {
  const { t } = useLocalization();
  const {
    setMenuStatus,
    sortOptions,
    selectedChoicesCount,
    selectedFacets,
    onRemoveFilter
  } = props;
  const dispatch = useAppDispatch();
  const { isCompareOpen } = useAppSelector((state) => state.compare);

  const showPills = typeof onRemoveFilter === 'function';

  const pills = showPills
    ? selectedFacets?.flatMap((facet) =>
        (facet?.data?.choices ?? [])
          .filter((choice) => choice.is_selected)
          .map((choice) => (
            <button
              type="button"
              key={`${facet.key}-${choice.value}`}
              className="flex items-center rounded-[50px] pl-4 pr-3 h-[1.875rem] border border-[#CDCFCE] text-xs/none hover:bg-gray-100"
              onClick={() => onRemoveFilter(facet, choice)}
              data-testid={`active-filter-${choice.label?.trim()}`}
            >
              {facet.name}: {choice.label} ({choice.quantity})
              <Icon name="close" size={18} className="ml-2 leading-none" />
            </button>
          ))
      )
    : null;

  const hasPills = Array.isArray(pills) && pills.length > 0;

  const hasFacets = useGroupedFacets();

  return (
    <>
      <div className="px-5 py-3 md:border-b md:border-[#CDCFCE] flex items-center justify-between text-primary-black md:px-0">
        <div className="flex flex-wrap items-center gap-2">
          {hasFacets.length > 0 && (
            <Button
              className="h-auto flex items-center rounded-none px-0 text-sm hover:bg-transparent hover:text-primary-black"
              onClick={() => setMenuStatus()}
              data-testid="list-filter"
              appearance="ghost"
            >
              <Icon name="filter" size={24} className="mr-2" />
              {t('category.filters.title')}
            </Button>
          )}
          {showPills ? (
            <div className="hidden md:flex flex-wrap items-center gap-2">
              {pills}
            </div>
          ) : (
            selectedChoicesCount > 0 && (
              <Button
                className="flex items-center rounded-[50px] pl-4 pr-3 h-[1.875rem] border border-[#CDCFCE] text-xs/none hover:bg-transparent hover:text-primary-black"
                onClick={() => setMenuStatus()}
                data-testid="list-filter"
                appearance="ghost"
              >
                {selectedChoicesCount}
                <Icon name="close" size={18} className="ml-2 leading-none" />
              </Button>
            )
          )}
        </div>
        <div className="flex items-center space-x-2 md:space-x-10">
          <Checkbox
            className="accent-primary-black text-sm pr-3"
            checked={isCompareOpen}
            onChange={() => dispatch(toggleCompare())}
          >
            Karşılaştır
          </Checkbox>
          <CategorySorter sortOptions={sortOptions} />
        </div>
      </div>
      {showPills && hasPills && (
        <div className="flex md:hidden flex-wrap items-center gap-2 px-5 py-3 border-b border-[#CDCFCE]">
          {pills}
        </div>
      )}
    </>
  );
};
